// src/features/explore/ExploreCoverThumb.tsx — shared cover renderer for ExploreCard and
// ExploreMediaPanel tiles (1.20.50).
//
// Priority chain: PostImage/media_key -> real <img> (object-fit: cover). Decorative post
// media (cityscape/globe) -> the existing decorative renderers, reused as-is. Text-only,
// OR an image cover whose url isn't a usable http(s) URL (empty string, relative path —
// the backend never guarantees a fully resolved URL), falls through to a deterministic
// decorative gradient + abstract motif keyed off the post id, so a card's look never
// changes between visits. An image load failure at runtime falls back the same way.
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { isHttpUrl } from '@/core/lib/isHttpUrl';
import { Cityscape } from '@/components/PostCard/media/decorative/Cityscape';
import { Globe } from '@/components/PostCard/media/decorative/Globe';
import { pickDecorativeCover, decorativeGradientCss } from './decorativeCover';
import { DecorativeMotif } from './DecorativeMotif';
import type { ExploreCover } from '@/core/models/explore';

const Fill = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

// Absolutely positioned (not a plain block img): the cover container is a fixed-height
// box already (ExploreCard's Thumb / the panel's square Tile), so this never contributes
// to layout — no reflow when the image finishes loading.
const CoverImg = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const GradientLayer = styled.div<{ $gradient: string }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${({ $gradient }) => $gradient};
`;

export interface ExploreCoverThumbProps {
  postId: string;
  cover: ExploreCover | undefined;
}

export function ExploreCoverThumb({ postId, cover }: ExploreCoverThumbProps) {
  const theme = useTheme();
  // Tracks the url that most recently failed, not a plain boolean — so a cover swap to a
  // DIFFERENT url (a refetched/edited post) is never stuck showing the previous url's
  // fallback for the rest of the session. `key={cover.url}` on the <img> below reinforces
  // this by remounting the element itself on a url change too.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const usableImage = cover?.kind === 'image' && isHttpUrl(cover.url);
  const imageFailed = usableImage && cover.url === failedUrl;

  if (usableImage && !imageFailed) {
    return (
      <Fill>
        <CoverImg
          key={cover.url}
          src={cover.url}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onError={() => setFailedUrl(cover.url)}
        />
      </Fill>
    );
  }

  if (cover?.kind === 'media') {
    return <Fill>{cover.variant === 'cityscape' ? <Cityscape fill /> : <Globe fill />}</Fill>;
  }

  const { gradientIndex, motif } = pickDecorativeCover(postId);
  const gradient = decorativeGradientCss(gradientIndex, theme.mode);
  return (
    <GradientLayer $gradient={gradient} aria-hidden="true">
      <DecorativeMotif motif={motif} mode={theme.mode} />
    </GradientLayer>
  );
}
